import { motion } from "framer-motion";
import { Music, Video, Image, ExternalLink } from "lucide-react";
import { useLang } from "@/lib/lang";

const mediaTypeIcons = {
  music: Music,
  video: Video,
  image: Image,
};

export default function MediaSection({ media }) {
  const { t } = useLang();
  const hasMedia = media && media.length > 0;

  return (
    <section id="media" data-testid="media-section" className="relative py-24 sm:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/18197126/pexels-photo-18197126.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
          }}
        />
        <div className="absolute inset-0 bg-[#05050A]/90" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="label-mono mb-4">{t("media_label")}</p>
          <h2
            data-testid="media-heading"
            className="font-['Unbounded'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white"
          >
            {t("media_heading")}
          </h2>
        </motion.div>

        {hasMedia ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {media.map((item, i) => {
              const Icon = mediaTypeIcons[item.media_type] || Music;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`media-card-${i}`}
                  className="glass-card rounded-3xl overflow-hidden group hover:-translate-y-2 transition-all duration-300 hover:border-[#FF007F]/30"
                >
                  {item.image_url && (
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] to-transparent" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#FF007F]/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#FF007F]" />
                      </div>
                      <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-widest text-[#FF007F]">
                        {item.media_type}
                      </span>
                    </div>
                    <h3 className="font-['Unbounded'] text-lg font-semibold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-[#A0A0AB] text-sm leading-relaxed mb-4">
                      {item.description}
                    </p>
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#FF007F] text-sm font-medium hover:text-white transition-colors"
                      >
                        Listen / Watch <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-3xl p-12 text-center max-w-2xl mx-auto"
          >
            <div className="w-16 h-16 rounded-2xl bg-[#FF007F]/10 flex items-center justify-center mx-auto mb-6">
              <Music className="w-8 h-8 text-[#FF007F]" />
            </div>
            <h3 className="font-['Unbounded'] text-2xl font-bold text-white mb-4">
              {t("media_coming_title")}
            </h3>
            <p className="text-[#A0A0AB] text-base leading-relaxed">
              {t("media_coming_text")}
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
